import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Store, UtensilsCrossed, Bike, QrCode, Megaphone, CreditCard, LayoutDashboard, Smartphone } from "lucide-react";

const APP_IMG = "https://images.unsplash.com/photo-1651804805680-3565ef7807f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxzbWFydHBob25lJTIwYXBwJTIwdXNlciUyMGluJTIwZGFya3xlbnwwfHx8fDE3ODY4MjE3ODR8MA&ixlib=rb-4.1.0&q=85";
const DELIVERY_IMG = "https://images.unsplash.com/photo-1695654390723-479197a8c4a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxmb29kJTIwZGVsaXZlcnklMjBkYXJrJTIwY2luZW1hdGljfGVufDB8fHx8MTc4NjgyMTc4NHww&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: Store, num: "01", title: "Storefront & Brand", href: "/features/website", span: "md:col-span-7", items: ["No-code homepage builder", "Full brand personalization", "Custom domain support", "Built-in SEO suite", "CMS content pages"] },
  { icon: UtensilsCrossed, num: "02", title: "Smart Menu & Ordering", href: "/features/smart-online-menu", span: "md:col-span-5", items: ["Modifiers, sizes & toppings", "Meal combos & deals", "Daypart menu scheduling", "Cuisine categorization", "Multi-language checkout"] },
  { icon: Bike, num: "03", title: "Fulfillment & Delivery", href: "/features/delivery-zones", span: "md:col-span-5", img: DELIVERY_IMG, items: ["Pickup & home delivery flows", "Zone & radius-based pricing", "Category delivery pricing", "Hours, windows & holidays"] },
  { icon: QrCode, num: "04", title: "QR & Dine-In", href: "/features/qr-dine-in-reservations", span: "md:col-span-7", items: ["Table QR self-ordering", "Online table reservations", "In-app promotion banners"] },
  { icon: Megaphone, num: "05", title: "Marketing & Loyalty", href: "/features/marketing-tools", span: "md:col-span-4", items: ["Automated rewards & points", "Promo codes & discounts", "Built-in email campaigns", "Digital gift cards"] },
  { icon: CreditCard, num: "06", title: "Payments & Alerts", href: "/features/shop", span: "md:col-span-4", items: ["Cards, wallets & cash", "Localized tax & fee config", "Instant SMS & email alerts"] },
  { icon: LayoutDashboard, num: "07", title: "Operations & Multi-Store", href: "/features/live-order-hub", span: "md:col-span-4", items: ["Real-time live order hub", "Automated kitchen printing", "Sales analytics & insights", "Multi-location control"] },
  { icon: Smartphone, num: "08", title: "Customer Mobile Apps", href: "/features/customer-apps", span: "md:col-span-12", img: APP_IMG, wide: true, items: ["Native iOS & Android apps", "Instant push notifications", "Dynamic app banner management", "App version control from admin", "One-tap re-ordering & fast checkout"] },
];

const ParallaxImg = ({ src, alt, className }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.img style={{ y }} src={src} alt={alt} className="w-full h-[120%] object-cover spotlight-img scale-110" />
    </div>
  );
};

export default function Features() {
  return (
    <section id="features" data-testid="features-section" className="max-w-7xl mx-auto px-6 lg:px-12 py-28 lg:py-40">
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="font-mono text-xs tracking-[0.3em] uppercase text-brand mb-6"
      >
        The System
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, delay: 0.1 }}
        className="font-display font-black uppercase tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl max-w-4xl text-ink"
      >
        Everything the platforms give you. <span className="text-steel">Minus the leash.</span>
      </motion.h2>

      <div className="mt-20 grid grid-cols-1 md:grid-cols-12 gap-6">
        {features.map((f, i) => (
          <motion.div
            key={f.num}
            data-testid={`feature-card-${f.num}`}
            role="link"
            tabIndex={0}
            onClick={() => { window.location.href = f.href; }}
            onKeyDown={(e) => { if (e.key === "Enter") window.location.href = f.href; }}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.05 * (i % 3) }}
            className={`group relative ${f.span} rounded-xl border border-ink/10 bg-white hover:border-brand/60 transition-colors duration-500 overflow-hidden cursor-pointer ${f.wide ? "md:flex" : ""}`}
          >
            <div className={`p-8 lg:p-10 ${f.wide ? "md:w-1/2" : ""}`}>
              <div className="flex items-center justify-between">
                <f.icon className="w-7 h-7 text-brand transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6" />
                <span className="font-mono text-xs text-ink/30">{f.num}</span>
              </div>
              <h3 className="mt-6 font-display font-black uppercase tracking-tight text-xl lg:text-2xl text-ink">{f.title}</h3>
              <ul className="mt-6 space-y-2.5">
                {f.items.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-ink/60 font-light">
                    <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-brand shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            {f.img && (
              <ParallaxImg
                src={f.img}
                alt={f.title}
                className={f.wide ? "md:w-1/2 min-h-[240px]" : "h-44 mt-2"}
              />
            )}
          </motion.div>
        ))}
      </div>
    </section>
  );
}
